import React, { Fragment, useEffect, useState } from 'react';
import { css } from 'twin.macro';
import { BootstrapToolTip } from '@/_elements/ToolTip';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Button, Form } from 'react-bootstrap';
import { BsTrash, BsGripVertical, BsFillPlusSquareFill } from 'react-icons/bs';
import { customCheckbox } from '@/_elements/CustomCheckbox';
import ID from '@/_helpers/uuid';
import OurAccordion from '@/_elements/OurAccordion';
import BlockInputFieldPreview from '../FormElements/BlockInputField/Preview';
import { getDateFromDateTime } from '@/_helpers';
import { getPreviewValueFromBlockInput } from '../PdfTemplatePreview/utils';

const cleanUpValue = (value) => {
    return String(value || '')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/[^a-zA-Z0-9\-!@#$%^&*()_+\[\]{}|:;<>? ]/g, '');
};

const DraggableBlockOptions = React.memo(
    ({ element, register, options = [], setOptions, handleRemoveOption, handleNewOption }) => {
        const [enableBlockInput, setEnableBlockInput] = useState((element?.options || {}).enableBlockInput || false);
        const [enableBlockPreview, setEnableBlockPreview] = useState(
            (element?.options || {}).enableBlockPreview ?? true
        );
        const sampleData =
            element?.key === 'DatePicker' ? getDateFromDateTime(new Date().toISOString()) : 'Lorem Ipsum';
        const [previewValues, setPreviewValues] = useState([]);

        useEffect(() => {
            setPreviewValues(getPreviewValueFromBlockInput({ data: options, value: sampleData, key: element?.key }));
        }, [options, sampleData]);

        useEffect(() => {
            if (enableBlockInput && options.length === 0) {
                if (element?.key === 'DatePicker') {
                    addYearMonthDay();
                } else {
                    handleNewOption({
                        text: 'New Block',
                        blockSize: 2,
                        value: ID.uuid(),
                    });
                }
            } else if (!enableBlockInput) {
                setOptions([]);
            }
        }, [enableBlockInput]);

        const handleDragEnd = (result) => {
            if (!result.destination) return;
            const reorderedOptions = Array.from(options);
            const [movedOption] = reorderedOptions.splice(result.source.index, 1);
            reorderedOptions.splice(result.destination.index, 0, movedOption);
            setOptions(reorderedOptions);
        };

        const handleLabelEdit = (e, optionId) => {
            const newValue = e.target.value;
            setOptions((prevOptions) =>
                prevOptions.map((option) => (option.value === optionId ? { ...option, text: newValue } : option))
            );
        };

        const handleBlockSizeEdit = (e, optionId) => {
            const newValue = cleanUpValue((e.target.value || '').trim());

            setOptions((prevOptions) =>
                prevOptions.map((option) =>
                    option.value === optionId ? { ...option, blockSize: Number(newValue || 0) } : option
                )
            );
        };

        const handleRegexEdit = (e, optionId) => {
            const newValue = e.target.value;
            setOptions((prevOptions) =>
                prevOptions.map((option) => (option.value === optionId ? { ...option, valueRegex: newValue } : option))
            );
        };

        const toggleBlockInput = (isChecked) => {
            setEnableBlockInput(isChecked);
        };

        const toggleBlockPreview = (isChecked) => {
            setEnableBlockPreview(isChecked);
        };

        const commonRegexPatterns = [
            ...(element?.key === 'DatePicker'
                ? [
                      { name: 'Day (DD)', pattern: '\\/(\\d{2})\\/' },
                      { name: 'Month (MM)', pattern: '^(\\d{2})\\/' },
                      { name: 'Year (YYYY)', pattern: '\\/(\\d{4})$' },
                      { name: 'Year (YY)', pattern: '(\\d{2})$' },
                  ]
                : []),
        ];

        const addYearMonthDay = async () => {
            handleNewOption({
                text: 'Month',
                blockSize: 2,
                value: ID.uuid(),
                valueRegex: commonRegexPatterns.find((p) => p?.name === 'Month (MM)').pattern,
            });
            handleNewOption({
                text: 'Day',
                blockSize: 2,
                value: ID.uuid(),
                valueRegex: commonRegexPatterns.find((p) => p?.name === 'Day (DD)').pattern,
            });
            handleNewOption({
                text: 'Year',
                blockSize: 4,
                value: ID.uuid(),
                valueRegex: commonRegexPatterns.find((p) => p?.name === 'Year (YYYY)').pattern,
            });
        };

        return (
            <React.Fragment>
                <div className="border p-2">
                    <OurAccordion title="Block Input Style" defaultShow={false}>
                        <div>
                            <Fragment>
                                <Form.Group
                                    controlId="enableBlockInput"
                                    css={css`
                                        display: flex;
                                        align-items: center;
                                        .requiredCheckbox {
                                            width: 20px;
                                            height: 20px;
                                        }
                                    `}
                                >
                                    <Form.Control
                                        className="requiredCheckbox"
                                        type="checkbox"
                                        css={customCheckbox}
                                        name="options.enableBlockInput"
                                        value={enableBlockInput}
                                        defaultChecked={enableBlockInput}
                                        onChange={(e) => {
                                            toggleBlockInput(e.target.checked);
                                        }}
                                        ref={register()}
                                    />
                                    <Form.Label
                                        css={css`
                                            margin: 0;
                                        `}
                                    >
                                        Enable Block Style Pdf Mapping
                                    </Form.Label>
                                </Form.Group>

                                <Form.Group
                                    controlId="enableBlockPreview"
                                    css={css`
                                        display: flex;
                                        align-items: center;
                                        .requiredCheckbox {
                                            width: 20px;
                                            height: 20px;
                                        }
                                    `}
                                >
                                    <Form.Control
                                        className="requiredCheckbox"
                                        type="checkbox"
                                        css={customCheckbox}
                                        name="options.enableBlockPreview"
                                        value={enableBlockPreview}
                                        defaultChecked={enableBlockPreview}
                                        onChange={(e) => {
                                            toggleBlockPreview(e.target.checked);
                                        }}
                                        ref={register()}
                                    />
                                    <Form.Label
                                        css={css`
                                            margin: 0;
                                        `}
                                    >
                                        Enable Block Preview In Form
                                    </Form.Label>
                                </Form.Group>
                            </Fragment>
                        </div>
                        {enableBlockInput && (options || [])?.length > 0 && (
                            <>
                                <div className="d-flex flex-row justify-content-between align-items-center">
                                    <div
                                        className="text-center w-100"
                                        css={css`
                                            flex: 2;
                                        `}
                                    >
                                        <strong>Block Label</strong>
                                    </div>
                                    <div
                                        className="text-center w-100"
                                        css={css`
                                            flex: 1;
                                        `}
                                    >
                                        <strong>Block Size</strong>
                                    </div>
                                    <div
                                        className="text-center w-100"
                                        css={css`
                                            flex: 2;
                                        `}
                                    >
                                        <strong>Regex (Optional)</strong>
                                    </div>
                                    <div className="ml-4 mr-4"></div>
                                </div>

                                <DragDropContext onDragEnd={handleDragEnd}>
                                    <Droppable droppableId="options">
                                        {(provided) => (
                                            <div {...provided.droppableProps} ref={provided.innerRef}>
                                                {options.map((option, index) => (
                                                    <Draggable
                                                        key={option.value}
                                                        draggableId={option.value}
                                                        index={index}
                                                    >
                                                        {(provided) => (
                                                            <div
                                                                className="d-flex mb-2"
                                                                ref={provided.innerRef}
                                                                {...provided.draggableProps}
                                                            >
                                                                <div
                                                                    {...provided.dragHandleProps}
                                                                    css={css`
                                                                        cursor: grab;
                                                                        display: flex;
                                                                        align-items: center;
                                                                        justify-content: center;
                                                                    `}
                                                                >
                                                                    <BsGripVertical size={24} />
                                                                </div>

                                                                <div
                                                                    css={css`
                                                                        display: flex;
                                                                        flex: 1;
                                                                        gap: 8px;
                                                                    `}
                                                                >
                                                                    {/* Block Label */}
                                                                    <Form.Control
                                                                        type="text"
                                                                        placeholder="Label"
                                                                        value={option.text}
                                                                        css={css`
                                                                            flex: 2;
                                                                        `}
                                                                        onChange={(e) =>
                                                                            handleLabelEdit(e, option.value)
                                                                        }
                                                                    />

                                                                    {/* Block Size */}
                                                                    <Form.Control
                                                                        type="number"
                                                                        placeholder="Size"
                                                                        value={option.blockSize || ''}
                                                                        css={css`
                                                                            flex: 1;
                                                                        `}
                                                                        onChange={(e) =>
                                                                            handleBlockSizeEdit(e, option.value)
                                                                        }
                                                                        min="1"
                                                                        max="10"
                                                                    />

                                                                    <div
                                                                        css={css`
                                                                            flex: 2;
                                                                            border: 1px solid #ccc;
                                                                            display: flex;
                                                                            align-items: center;
                                                                            justify-content: space-between;
                                                                        `}
                                                                    >
                                                                        <Form.Control
                                                                            type="text"
                                                                            placeholder="Custom regex"
                                                                            value={option.valueRegex || ''}
                                                                            onChange={(e) =>
                                                                                handleRegexEdit(e, option.value)
                                                                            }
                                                                            disabled={
                                                                                option.validation &&
                                                                                option.validation !== 'custom'
                                                                            }
                                                                        />
                                                                        <Form.Control
                                                                            css={css`
                                                                                flex: 1;
                                                                                max-width: 120px;
                                                                            `}
                                                                            as="select"
                                                                            onChange={(e) => {
                                                                                if (e.target.value) {
                                                                                    handleRegexEdit(
                                                                                        {
                                                                                            target: {
                                                                                                value: commonRegexPatterns.find(
                                                                                                    (p) =>
                                                                                                        p.name ===
                                                                                                        e.target.value
                                                                                                ).pattern,
                                                                                            },
                                                                                        },
                                                                                        option.value
                                                                                    );
                                                                                }
                                                                            }}
                                                                            value=""
                                                                        >
                                                                            <option value=""></option>
                                                                            {commonRegexPatterns.map((pattern) => (
                                                                                <option
                                                                                    key={pattern.name}
                                                                                    value={pattern.name}
                                                                                >
                                                                                    {pattern.name}
                                                                                </option>
                                                                            ))}
                                                                        </Form.Control>
                                                                    </div>
                                                                </div>

                                                                <div className="ml-2"></div>

                                                                {/* Only show delete button if there's more than one option */}
                                                                {options.length > 1 && (
                                                                    <div
                                                                        css={css`
                                                                            padding: 2px;
                                                                            cursor: pointer;
                                                                            margin-left: 8px;
                                                                            display: flex;
                                                                            align-items: center;
                                                                        `}
                                                                    >
                                                                        <BootstrapToolTip
                                                                            text={'Delete'}
                                                                            side={'top'}
                                                                            cssStyles={
                                                                                'top:-70% !important; min-width: fit-content !important'
                                                                            }
                                                                        >
                                                                            <BsTrash
                                                                                color="red"
                                                                                size="24"
                                                                                onClick={() =>
                                                                                    handleRemoveOption(option.value)
                                                                                }
                                                                            />
                                                                        </BootstrapToolTip>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </Draggable>
                                                ))}
                                                {provided.placeholder}
                                            </div>
                                        )}
                                    </Droppable>
                                </DragDropContext>

                                {element?.key === 'DatePicker' && (options || []).length > 2 ? null : (
                                    <div className="mb-4 w-full flex justify-end">
                                        <div
                                            css={css`
                                                width: 36px;
                                                margin-left: auto;
                                            `}
                                        >
                                            <BootstrapToolTip text={'Add New Block'} side={'top'}>
                                                <BsFillPlusSquareFill
                                                    color="#50aeb0"
                                                    size="24"
                                                    className="ml-auto"
                                                    css={css`
                                                        font-size: 2rem;
                                                        border-radius: 50%;
                                                        width: 36px;
                                                        height: 36px;
                                                        cursor: pointer;
                                                    `}
                                                    onClick={() =>
                                                        handleNewOption({
                                                            text: 'New Block',
                                                            blockSize: 2,
                                                            value: ID.uuid(),
                                                        })
                                                    }
                                                />
                                            </BootstrapToolTip>
                                        </div>

                                        {element?.key === 'DatePicker' && options?.length === 0 && (
                                            <div
                                                css={css`
                                                    width: 300px;
                                                    margin-left: auto;
                                                    margin-top: 10px;
                                                `}
                                            >
                                                <Button
                                                    css={css`
                                                        width: 300px;
                                                    `}
                                                    onClick={() => {
                                                        addYearMonthDay();
                                                    }}
                                                >
                                                    Add DD/MM/YYYY Blocks
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                )}
                                <hr />
                                <div
                                    className="mb-4 d-flex align-items-center justify-content-between"
                                    style={{ marginTop: '30px' }}
                                >
                                    <h2>Preview Blocks:</h2>
                                    <div>
                                        <div>
                                            <Fragment>
                                                Sample Data:
                                                <span className="ml-2">"{sampleData}"</span>
                                            </Fragment>
                                        </div>

                                        <BlockInputFieldPreview
                                            key={JSON.stringify(options)}
                                            componentErrorMessage={
                                                'Failed to preview blocks. Please enter valid fields'
                                            }
                                            data={{
                                                data: options,
                                                value: JSON.stringify(previewValues),
                                            }}
                                            isPreview={true}
                                        />
                                    </div>
                                </div>
                            </>
                        )}
                    </OurAccordion>
                </div>
            </React.Fragment>
        );
    }
);
export default DraggableBlockOptions;
